import { useCallback, useState } from 'react';
import { Button, Layout } from 'antd';
import StackedStones from '../../assets/images/stacked-stones.png';
import ScreeningIcon from '../../assets/images/screening-button-icon.svg';
import ScreeningWhiteIcon from '../../assets/images/screening-button-white-icon.svg';
import PreworkIcon from '../../assets/images/prework-button-icon.svg';
import PreworkWhiteIcon from '../../assets/images/prework-button-white-icon.svg';
import NewPreceptorSupportIcon from '../../assets/images/nps-button-icon.svg';
import NewPreceptorSupportWhiteIcon from '../../assets/images/nps-button-white-icon.svg';
import ExamModuleIcon from '../../assets/images/exam-module-button-icon.svg';
import ExamModuleWhiteIcon from '../../assets/images/exam-module-button-white-icon.svg';
import ManageFunctionariesIcon from '../../assets/images/manage-functionaries-button-icon.svg';
import ManageFunctionariesWhiteIcon from '../../assets/images/manage-functionaries-button-white-icon.svg';
import LogoutImage from '../../assets/images/logout-red-icon.svg';
import { useTranslation } from 'react-i18next';
import { useRedirectToLogin } from '../../hooks/useRedirectToLogin';
import { UserRole } from '../../shared/VolunteerApplicationServiceTypes';
import Icon from '../../components/ui-components/Icon';
import { useGroupId } from '../../hooks/useGroupId';
import { usePreworkGroupId } from '../../hooks/usePreworkGroupId';
import {
	EXAM_MODULE_HOME,
	FUNCTIONARY_HOME,
	GROUPS,
	PREWORKGROUPS,
	VIEW_FUNCTIONARIES,
} from '../../shared/Routes';
import { GroupTabs, PreworkGroupTabs } from '../../shared/Constants';

type Module = {
	key: string;
	label: string;
	icon: React.ReactNode;
	hoverIcon: React.ReactNode;
	onClick: () => void;
};

export const SelectModule = () => {
	const { t } = useTranslation();
	const { role, logout } = useRedirectToLogin();
	const [selectedModule, setSelectedModule] = useState<string>();
	const [hoveredModule, setHoveredModule] = useState<string | null>(null);
	const { navigateWithBatchIdAndGroupTab } = useGroupId();
	const { navigateWithPreworkBatchIdAndGroupTab } = usePreworkGroupId();
	const isAdmin = role === UserRole.ADMIN;
	const isPreworkAdmin = role === UserRole.PREWORK_ADMIN;
	let roleName;
	if (role === UserRole.APPROVER) {
		roleName = 'AC';
	} else if (role === UserRole.ZC) {
		roleName = UserRole.ZC;
	} else if (role === UserRole.PREWORK_ADMIN) {
		roleName = 'Prework Admin';
	} else if (role === UserRole.ADMIN) {
		roleName = 'Admin';
	}

	const handleScreeningClick = useCallback(() => {
		navigateWithBatchIdAndGroupTab(FUNCTIONARY_HOME);
	}, [navigateWithBatchIdAndGroupTab]);

	const handleNewPreceptorSupportClick = useCallback(() => {
		navigateWithBatchIdAndGroupTab(GROUPS, GroupTabs.CREATE_GROUP);
	}, [navigateWithBatchIdAndGroupTab]);

	const handleManageFunctionary = useCallback(() => {
		navigateWithBatchIdAndGroupTab(VIEW_FUNCTIONARIES);
	}, [navigateWithBatchIdAndGroupTab]);

	const handlePreWorkClick = useCallback(() => {
		navigateWithPreworkBatchIdAndGroupTab(
			PREWORKGROUPS,
			PreworkGroupTabs.CREATE_GROUP
		);
	}, [navigateWithPreworkBatchIdAndGroupTab]);

	const handleExamModuleClick = useCallback(() => {
		navigateWithBatchIdAndGroupTab(EXAM_MODULE_HOME);
	}, [navigateWithBatchIdAndGroupTab]);

	const handleLogoutClick = useCallback(() => {
		if (logout) {
			void logout();
		}
	}, [logout]);

	const modules: Module[] = [
		!isPreworkAdmin && {
			key: 'screening',
			label: 'Screening',
			icon: <Icon src={ScreeningIcon} className="w-4 h-4 flex items-center" />,
			hoverIcon: (
				<Icon src={ScreeningWhiteIcon} className="w-4 h-4 flex items-center" />
			),
			onClick: handleScreeningClick,
		},
		(isAdmin || isPreworkAdmin) && {
			key: 'prework',
			label: 'Pre work',
			icon: <Icon src={PreworkIcon} className="w-4 h-4 flex items-center" />,
			hoverIcon: (
				<Icon src={PreworkWhiteIcon} className="w-4 h-4 flex items-center" />
			),
			onClick: handlePreWorkClick,
		},
		!isPreworkAdmin && {
			key: 'support',
			label: 'New Preceptor Support',
			icon: (
				<Icon
					src={NewPreceptorSupportIcon}
					className="w-4 h-4 flex items-center"
				/>
			),
			hoverIcon: (
				<Icon
					src={NewPreceptorSupportWhiteIcon}
					className="w-4 h-4 flex items-center"
				/>
			),
			onClick: handleNewPreceptorSupportClick,
		},
		isAdmin && {
			key: 'exam',
			label: 'Exam Module',
			icon: <Icon src={ExamModuleIcon} className="w-4 h-4 flex items-center" />,
			hoverIcon: (
				<Icon src={ExamModuleWhiteIcon} className="w-4 h-4 flex items-center" />
			),
			onClick: handleExamModuleClick,
		},
		isAdmin && {
			key: 'manage',
			label: 'Manage Functionaries',
			icon: (
				<Icon
					src={ManageFunctionariesIcon}
					className="w-4 h-4 flex items-center"
				/>
			),
			hoverIcon: (
				<Icon
					src={ManageFunctionariesWhiteIcon}
					className="w-4 h-4 flex items-center"
				/>
			),
			onClick: handleManageFunctionary,
		},
	].filter(Boolean) as Module[];

	return (
		<Layout className="bg-bground-blue flex items-center py-10">
			<div className="bg-white rounded-2xl md:shadow-md w-full max-w-5xl h-full max-h-[85vh] flex flex-col md:flex-row overflow-hidden">
				{/* Left Image Section - Desktop Only */}
				<div className="hidden md:flex md:w-1/2 p-8 justify-center items-center">
					<img
						src={StackedStones}
						alt="Self-Nominating Preceptor"
						className="max-h-full max-w-full rounded-2xl"
					/>
				</div>

				{/* Right Content */}
				<div className="flex flex-col items-center justify-center w-full md:w-1/2 p-6 md:p-10">
					<h1 className="text-2xl md:text-3xl font-bold font-sans mb-2 text-center">
						{t('welcomeRCAC', { roleName })}
					</h1>
					<p className="font-poppins font-medium text-[#4C4748] mb-6 text-center">
						{t('pleaseSelectAModule')}
					</p>

					<div className="flex flex-col gap-4 w-[80%]">
						{modules.map(({ key, label, icon, hoverIcon, onClick }) => {
							const isSelected = selectedModule === key;
							const isHovered = hoveredModule === key;

							return (
								<Button
									key={key}
									icon={isHovered || isSelected ? hoverIcon : icon}
									size="large"
									type="primary"
									className="w-full h-[50px] font-sans font-semibold text-base text-gray-650 bg-white border border-solid border-[#D0D5DD] hover:bg-primary-blue shadow-none focus:shadow-none active:shadow-none"
									onMouseEnter={() => setHoveredModule(key)}
									onMouseLeave={() => setHoveredModule(null)}
									onClick={() => {
										setSelectedModule(key);
										onClick?.();
									}}
								>
									{label}
								</Button>
							);
						})}
					</div>

					<div className="mt-6 text-center">
						<Button
							icon={
								<Icon src={LogoutImage} className="w-4 h-4 flex items-center" />
							}
							type="text"
							danger
							className="font-sans font-semibold text-base h-[50px] hover:py-4 text-red-600 hover:text-red-700"
							onClick={handleLogoutClick}
						>
							{t('logout')}
						</Button>
					</div>
				</div>
			</div>
		</Layout>
	);
};
